'use client';

import {
  useRef,
  useState,
  type ChangeEvent,
  type FC,
  type KeyboardEvent
} from 'react';
import { observer } from 'mobx-react-lite';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { useLocaleMessage } from '@/react/hooks/useLocaleMessage';
import type { SearchBoxVM } from '@/react/view-models/SearchBoxVM';
import { Icon, IconSizeProp, IconTypes } from '../../core-ui/Icon';
import { Button } from '../../core-ui/Button';

import S from './styles.base.module.scss';
import { search_box_placeholder } from "@/lang/__generated__/ahnu/search_box_placeholder";
import { search_box_search } from "@/lang/__generated__/ahnu/search_box_search";

/**
 * Props for the {@link SearchInput} component.
 */
export interface ISearchInputProps {
  /**
   * The SearchBoxVM instance from the parent component.
   * This allows the parent to share state with other components that depend on it.
   */
  vm: SearchBoxVM;

  /**
   * Callback invoked when the user submits a search query.
   * The parent component should handle navigation to the search results page.
   *
   * @param term - The search term entered by the user.
   */
  onNavigate: (term: string) => void;

  /**
   * Indicates whether the parent is currently navigating to the search results page.
   * When true, the input will be disabled to prevent duplicate submissions.
   */
  isNavigating: boolean;

  /**
   * Optional placeholder text for the search input.
   * If not provided, defaults to the localized 'search.box.placeholder' message.
   */
  placeholder?: string;

  /**
   * Optional className to apply to the container element.
   */
  className?: string;

  /**
   * Optional className to apply to the input element.
   */
  inputClassName?: string;

  /**
   * Optional className to apply to the search button.
   */
  buttonClassName?: string;
}

/**
 * A simple, standalone search input component with a search button.
 *
 * This component provides a basic search input field that navigates to the search
 * results page when the user submits a query. It does **not** integrate with the
 * search popover or display search suggestions.
 *
 * **Key differences from {@link SearchBox}:**
 * - Does not show a search suggestions popover
 * - Does not display product suggestions while typing
 * - Does not integrate with the search suggestions/autocomplete system
 * - Simpler, lightweight implementation for use cases where only basic
 *   search navigation is needed (e.g., error pages, no results pages)
 * - Requires the parent to provide `vm`, `onNavigate`, and `isNavigating` props
 *   for consistent state management across sibling components
 *
 * For a full-featured search experience with autocomplete and suggestions,
 * use the {@link SearchBox} component instead.
 *
 * @example
 * ```tsx
 * const [vm, { navigate, isNavigatingToResults }] = useSearchBox();
 *
 * <SearchInput
 *   vm={vm}
 *   onNavigate={navigate}
 *   isNavigating={isNavigatingToResults}
 *   placeholder="Search products..."
 *   className={styles.searchContainer}
 * />
 * ```
 */
export const SearchInput: FC<ISearchInputProps> = observer(
  function SearchInput({
    vm,
    onNavigate,
    isNavigating,
    placeholder,
    className,
    inputClassName,
    buttonClassName
  }) {
    const [msg] = useLocaleMessage();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const handleEnterSearch = (): void => {
      // Do not search if the input is empty
      if (searchTerm.length === 0 || isNavigating) {
        return;
      }

      // Unfocus the input since the user is done typing
      inputRef?.current?.blur();

      // Delegate navigation to the parent component
      onNavigate(searchTerm);
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
      switch (e.key) {
        case 'Enter':
          handleEnterSearch();
          break;
        case 'Escape':
          inputRef?.current?.blur();
          break;
        default:
          break;
      }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
      if (isNavigating) {
        // If the search navigation is already in progress, do not trigger another search
        return;
      }

      setSearchTerm(e.target.value);
    };

    const isSearchButtonDisabled = searchTerm.length === 0 || isNavigating;

    return (
      <div className={classes(S.container, className)}>
        <input
          ref={inputRef}
          className={classes(S.input, inputClassName)}
          placeholder={placeholder ?? msg(search_box_placeholder)}
          value={searchTerm}
          onKeyDown={handleKeyPress}
          onChange={handleInputChange}
          aria-label={placeholder ?? msg(search_box_placeholder)}
        />
        <Button
          title={msg(search_box_search)}
          datatestID="searchInputButton"
          variant="text"
          onClick={handleEnterSearch}
          className={classes(S.searchButton, buttonClassName)}
          disabled={isSearchButtonDisabled}
          ariaLabel={msg(search_box_search)}
        >
          <Icon size={IconSizeProp.SizeHeavy} icon={IconTypes.Search} />
        </Button>
      </div>
    );
  }
);
